import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { PageHeader, Loading, StatRow } from "@/components/Common";
import { EUR, EURc, pct, pnlColor, fmtDate } from "@/lib/format";
import api from "@/lib/api";

const PERIODS = [
  { k: "today", l: "Oggi" }, { k: "7d", l: "7G" }, { k: "30d", l: "30G" },
  { k: "3m", l: "3M" }, { k: "6m", l: "6M" }, { k: "1y", l: "1A" }, { k: "all", l: "Tutto" },
];

export default function Performance() {
  const [period, setPeriod] = useState("all");
  const { data, isLoading } = useQuery({
    queryKey: ["performance", period],
    queryFn: () => api.get(`/performance?period=${period}`).then((r) => r.data),
  });

  if (isLoading || !data) return <Loading />;
  const s = data.stats;
  const dd = data.drawdown;
  const curve = (data.equity_curve || []).map((p) => ({
    time: p.time.slice(0, 10), balance: p.balance, drawdown: -p.drawdown,
  }));

  return (
    <div data-testid="performance-page">
      <PageHeader title="Performance" subtitle="Equity curve, drawdown e statistiche complete">
        <div className="flex gap-1 rounded-lg border border-white/10 bg-[#0F0F12] p-1">
          {PERIODS.map((p) => (
            <button key={p.k} onClick={() => setPeriod(p.k)} data-testid={`equity-chart-filter-${p.k}`}
              className={`rounded-md px-3 py-1.5 font-sans text-xs font-medium transition-colors ${period === p.k ? "bg-gold/15 text-gold" : "text-zinc-400 hover:text-white"}`}>
              {p.l}
            </button>
          ))}
        </div>
      </PageHeader>

      <div className="dlx-card dlx-grain mb-6 p-5 sm:p-6" data-testid="equity-curve-chart">
        <div className="mb-4 flex items-end justify-between">
          <div>
            <div className="font-sans text-[11px] uppercase tracking-[0.25em] text-zinc-500">Equity Curve</div>
            <div className={`mt-1 font-mono text-2xl font-bold ${pnlColor(s.net_pnl)}`}>{EURc(s.net_pnl)}</div>
          </div>
          <div className="text-right">
            <div className="font-sans text-[11px] uppercase tracking-widest text-zinc-500">Return</div>
            <div className={`font-mono text-lg ${pnlColor(data.return_pct)}`}>{pct(data.return_pct)}</div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={curve} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="eq" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
            <XAxis dataKey="time" tick={{ fill: "#52525B", fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={40} />
            <YAxis tick={{ fill: "#52525B", fontSize: 10 }} tickLine={false} axisLine={false} width={60}
              tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} domain={["dataMin", "dataMax"]} />
            <Tooltip contentStyle={{ background: "#0F0F12", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 8, fontSize: 12 }}
              formatter={(v) => [EUR(v), "Balance"]} />
            <Area type="monotone" dataKey="balance" stroke="#D4AF37" strokeWidth={2} fill="url(#eq)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="dlx-card p-5 sm:p-6" data-testid="drawdown-panel">
          <div className="mb-4 font-sans text-[11px] uppercase tracking-[0.25em] text-zinc-500">Drawdown</div>
          <StatRow label="Drawdown attuale" value={EUR(dd.current_drawdown)} tone={dd.current_drawdown > 0 ? "loss" : undefined} />
          <StatRow label="Max Drawdown" value={`${EUR(dd.max_drawdown)} · ${dd.max_drawdown_pct}%`} tone="loss" />
          <StatRow label="Durata max DD" value={`${dd.max_drawdown_duration_days} giorni`} />
          <div className="mt-4 h-24">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={curve} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                <defs>
                  <linearGradient id="ddg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#EF4444" stopOpacity={0} />
                    <stop offset="100%" stopColor="#EF4444" stopOpacity={0.35} />
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="drawdown" stroke="#EF4444" strokeWidth={1.5} fill="url(#ddg)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="dlx-card p-5 sm:p-6 lg:col-span-2" data-testid="performance-stats">
          <div className="mb-4 font-sans text-[11px] uppercase tracking-[0.25em] text-zinc-500">Statistiche</div>
          <div className="grid gap-x-8 sm:grid-cols-2">
            <div>
              <StatRow label="Net P&L" value={EURc(s.net_pnl)} tone={s.net_pnl >= 0 ? "profit" : "loss"} />
              <StatRow label="Return %" value={pct(data.return_pct)} />
              <StatRow label="Win Rate" value={`${s.win_rate}%`} />
              <StatRow label="Average Win" value={EUR(s.avg_win)} tone="profit" />
              <StatRow label="Average Loss" value={EUR(s.avg_loss)} tone="loss" />
              <StatRow label="Risk / Reward" value={s.risk_reward.toFixed(2)} />
            </div>
            <div>
              <StatRow label="Profit Factor" value={s.profit_factor.toFixed(2)} />
              <StatRow label="Expectancy" value={EUR(s.expectancy)} />
              <StatRow label="Largest Win" value={EURc(s.largest_win)} tone="profit" />
              <StatRow label="Largest Loss" value={EURc(s.largest_loss)} tone="loss" />
              <StatRow label="Winning Streak" value={s.winning_streak} />
              <StatRow label="Losing Streak" value={s.losing_streak} />
            </div>
          </div>
          <div className="mt-3 border-t border-white/5 pt-3">
            <StatRow label="Average Trade Duration" value={`${s.avg_duration_min} min`} />
            <StatRow label="Average R" value={s.avg_r.toFixed(2)} tone={s.avg_r >= 0 ? "profit" : "loss"} />
          </div>
        </div>
      </div>
    </div>
  );
}
